import { useRef } from "react";
import { Link } from "@/components/LangLink";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowUpRight, ArrowRight, ShieldCheck, Stethoscope, Ruler, MapPin,
  Home as HomeIcon, HeartPulse, Award, Clock,
  FileText, ListChecks, CheckCircle2, Heart, Check, Target, Eye,
} from "lucide-react";
import { MaskLine, Reveal, Stagger, StaggerItem } from "@/components/primitives";
import { SectionHeading, Marquee, CTASection, BrandCard, CheckList, SolutionsShowcase, HistoryTimeline, Testimonials, BrandsStrip } from "@/components/sections";
import { CertificationsHomeCallout } from "@/components/Certifications";
import QuickBooking from "@/components/QuickBooking";
import { IMG, STATS, BRANDS, BRANDS_SECONDARY, METHOD_FLOW, METHOD_BENEFITS, WHY, COMPANY_INTRO, HOME_ABOUT, MISSION, VISION } from "@/lib/site";

const WHY_ICONS = [Clock, ShieldCheck, Award, Stethoscope, Ruler, HomeIcon, HeartPulse, MapPin];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section ref={ref} className="relative overflow-hidden min-h-screen flex items-center grain-overlay" data-testid="hero">
      <div className="absolute inset-0" style={{ background: "linear-gradient(135deg,#0F1923 0%,#12284C 45%,#1B3A6B 100%)" }} />
      <div className="absolute top-1/3 -left-32 w-[30rem] h-[30rem] rounded-full bg-green/20 blur-[130px]" />

      <div className="container-x relative pt-32 pb-20 lg:py-32 grid lg:grid-cols-[1.15fr_0.85fr] gap-12 items-center">
        {/* Left */}
        <div>
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/80 backdrop-blur"
            data-testid="hero-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-green animate-pulse" />
            Dal 1986 — Centro Specializzato
          </motion.span>

          <h1 className="mt-7 font-serif text-[2.6rem] leading-[1.06] sm:text-5xl lg:text-6xl xl:text-[4.2rem] font-bold text-white tracking-tight">
            <MaskLine delay={0.15}>La terapia</MaskLine>
            <MaskLine delay={0.27}>elasto-compressiva</MaskLine>
            <MaskLine delay={0.39}>ha un centro</MaskLine>
            <MaskLine delay={0.51} className="text-green">di riferimento.</MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.75, duration: 0.7 }}
            className="mt-6 font-serif italic text-lg lg:text-xl text-green"
          >
            Affidabilità che si indossa.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <Link to="/il-metodo#prenota" data-testid="hero-cta-primary" className="btn-green">
              Prenota Misurazione <ArrowRight size={16} />
            </Link>
            <Link to="/#il-metodo" data-testid="hero-cta-secondary" className="btn-light">
              Il nostro metodo
            </Link>
          </motion.div>

          {/* floating stat pills */}
          <div className="mt-12 flex flex-wrap gap-4">
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.05 + i * 0.12, duration: 0.6 }}
                className="rounded-2xl border border-white/15 bg-white/8 px-6 py-4 backdrop-blur-md"
                data-testid={`hero-stat-${i}`}
              >
                <div className="font-serif text-2xl lg:text-3xl font-bold text-white">{s.value}</div>
                <div className="mt-1 text-[11px] uppercase tracking-wider text-white/60">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Right image with parallax */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 1 }}
          className="relative mt-10 lg:mt-0"
        >
          <div className="relative overflow-hidden rounded-[2rem] border border-white/10 shadow-[0_40px_90px_-30px_rgba(0,0,0,0.7)]">
            <motion.img
              src={IMG.heroLegs}
              alt="Terapia elasto-compressiva"
              style={{ scale }}
              className="w-full h-[320px] sm:h-[420px] lg:h-[560px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent" />
          </div>
          <motion.div
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute left-4 bottom-4 lg:-left-8 lg:bottom-16 rounded-2xl border border-white/15 bg-white/10 backdrop-blur-xl px-4 py-3 lg:px-5 lg:py-4 shadow-xl"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 lg:h-10 lg:w-10 items-center justify-center rounded-full bg-green text-white"><ShieldCheck size={17} /></span>
              <div>
                <div className="text-white text-sm font-semibold">Misura certificata</div>
                <div className="text-white/60 text-xs">Prova sempre inclusa</div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const Dal1992 = () => (
  <section className="py-20 lg:py-28 bg-white" data-testid="section-1992">
    <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
      <div>
        <span className="eyebrow">Dal 1986</span>
        <Reveal delay={0.05}>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-[1.1]">
            Oltre quarant’anni dedicati al benessere della tua circolazione.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
            {HOME_ABOUT}
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Perché la calza giusta non si sceglie: si misura. Un unico ambito, portato all’eccellenza,
            con la stessa cura dal primo giorno.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <Link to="/#chi-siamo" className="mt-8 inline-flex items-center gap-2 font-semibold text-green hover:gap-3 transition-all" data-testid="link-chi-siamo">
            La nostra storia <ArrowUpRight size={18} />
          </Link>
        </Reveal>
      </div>
      <Reveal delay={0.1} className="relative">
        <div className="overflow-hidden rounded-[1.75rem] border border-[#E7EDF2] shadow-[0_30px_70px_-30px_rgba(15,25,35,0.25)]">
          <img src={IMG.consultation} alt="Consulenza specialistica" className="w-full h-[460px] object-cover" />
        </div>
        <div className="absolute -bottom-6 -left-6 rounded-2xl bg-navy text-white px-7 py-5 shadow-xl">
          <div className="font-serif text-4xl font-bold">40<span className="text-green">+</span></div>
          <div className="text-xs uppercase tracking-wider text-white/60 mt-1">Anni di esperienza</div>
        </div>
      </Reveal>
    </div>
  </section>
);

const ChiSiamoHome = () => (
  <section id="chi-siamo" className="scroll-mt-28 py-20 lg:py-28 bg-cloud" data-testid="section-chi-siamo">
    <div className="container-x">
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <div className="relative order-2 lg:order-1">
          <div className="overflow-hidden rounded-[1.75rem] border border-[#E7EDF2] shadow-[0_30px_70px_-30px_rgba(15,25,35,0.25)]">
            <img src={IMG.clinic} alt="La sede Medisistem" className="w-full h-[480px] object-cover" />
          </div>
        </div>
        <div className="order-1 lg:order-2">
          <span className="eyebrow">Chi Siamo</span>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-[1.1]">
            Chi è Medisistem.
          </h2>
          <div className="mt-6 space-y-4 text-muted-foreground leading-relaxed" data-testid="chi-siamo-intro">
            {COMPANY_INTRO.map((p, i) => (
              <p key={i} className={i === 0 ? "text-lg" : ""}>{p}</p>
            ))}
            <p className="font-serif italic text-green text-lg">Affidabilità che si indossa.</p>
          </div>
        </div>
      </div>

      {/* Mission & Vision — compatte */}
      <div className="mt-12 grid md:grid-cols-2 gap-5 max-w-4xl">
        <div className="rounded-2xl bg-navy p-6 text-white" data-testid="home-mission">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-green"><Target size={18} /></span>
            <h3 className="font-serif text-lg font-bold">Mission</h3>
          </div>
          <p className="mt-3 text-sm text-white/75 leading-relaxed">{MISSION}</p>
        </div>
        <div className="rounded-2xl bg-green-deep p-6 text-white" data-testid="home-vision">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-white"><Eye size={18} /></span>
            <h3 className="font-serif text-lg font-bold">Vision</h3>
          </div>
          <p className="mt-3 text-sm text-white/85 leading-relaxed">{VISION}</p>
        </div>
      </div>

      {/* Timeline */}
      <div className="mt-16">
        <h3 className="font-serif text-2xl lg:text-3xl font-bold text-navy">Dal 1986 ad oggi</h3>
        <p className="mt-2 text-muted-foreground">Un percorso di specializzazione continua, anno dopo anno.</p>
        <div className="mt-10">
          <HistoryTimeline />
        </div>
      </div>

      <div className="mt-12">
        <Link to="/chi-siamo" data-testid="home-chisiamo-cta" className="btn-outline">
          Scopri di più su di noi <ArrowUpRight size={16} />
        </Link>
      </div>
    </div>
  </section>
);

const Soluzioni = () => (
  <section className="py-20 lg:py-28 bg-white" data-testid="section-soluzioni">
    <div className="container-x">
      <SectionHeading eyebrow="Le nostre soluzioni" title="Le calze sono il cuore della nostra offerta." intro="Il fulcro della terapia elasto-compressiva, con bendaggi e pressoterapia a completamento del percorso." />
      <div className="mt-14">
        <SolutionsShowcase />
      </div>
      <div className="mt-12 flex justify-center">
        <Link to="/prodotti" data-testid="home-explore-products" className="btn-outline">
          Esplora tutti i prodotti <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  </section>
);

const BrandsSection = () => (
  <section className="py-20 lg:py-28 bg-cloud" data-testid="section-brand">
    <div className="container-x">
      <SectionHeading eyebrow="I nostri brand" title="Marchi che scegliamo — e che creiamo." intro="Un marchio di proprietà e la distribuzione esclusiva di un'eccellenza tedesca." />
      <div className="mt-14 grid lg:grid-cols-2 gap-6">
        {BRANDS.map((b) => <BrandCard key={b.id} brand={b} />)}
      </div>
      <div className="mt-6 grid sm:grid-cols-2 gap-6">
        {BRANDS_SECONDARY.map((b) => (
          <Link key={b.id} to={b.to} data-testid={`brand-secondary-${b.id}`} className="card-clean flex items-center justify-between p-7 group">
            <div>
              <h4 className="font-serif text-xl font-bold text-navy">{b.name}</h4>
              <p className="mt-1 text-sm text-muted-foreground">{b.desc}</p>
            </div>
            <ArrowUpRight size={22} className="text-green transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const Differenza = () => (
  <section className="relative overflow-hidden py-20 lg:py-28" data-testid="section-differenza">
    <div className="absolute inset-0" style={{ background: "linear-gradient(135deg,#1F5C48 0%,#175340 60%,#12362b 100%)" }} />
    <div className="absolute inset-0 grain-overlay" />
    <div className="container-x relative grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <span className="eyebrow text-white/70">La nostra differenza</span>
        <Reveal delay={0.05}>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.1]">
            La misura giusta fa la differenza.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-white/75 text-lg leading-relaxed">
            Una compressione efficace nasce da una misurazione precisa. Da noi non si acquista al buio:
            si misura, si prova, si sceglie con cura.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-8 flex flex-wrap gap-3">
            {["Misurazione professionale", "Prova della calza", "In sede o a domicilio"].map((p) => (
              <span key={p} className="rounded-full border border-white/25 bg-white/5 px-5 py-2.5 text-sm text-white/90 backdrop-blur">{p}</span>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <Link to="/il-metodo#prenota" data-testid="differenza-cta" className="mt-9 inline-flex btn-light border-white/40">
            Prenota la tua misurazione <ArrowRight size={16} />
          </Link>
        </Reveal>
      </div>
      <Reveal delay={0.1}>
        <div className="overflow-hidden rounded-[1.75rem] border border-white/10 shadow-2xl">
          <img src={IMG.measure} alt="Misurazione professionale" className="w-full h-[420px] object-cover" />
        </div>
      </Reveal>
    </div>
  </section>
);

const METODO_ICONS = [FileText, Ruler, ListChecks, CheckCircle2, Heart];

const MetodoHome = () => (
  <>
    <section id="il-metodo" className="scroll-mt-28 relative overflow-hidden py-20 lg:py-28 bg-ink" data-testid="section-metodo">
      <div className="absolute inset-0" style={{ background: "linear-gradient(160deg,#0F1923 0%,#12284C 100%)" }} />
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-green/15 blur-[120px]" />
      <div className="container-x relative">
        <div className="max-w-3xl">
          <SectionHeading eyebrow="Il Metodo Medisistem" title="La misura giusta fa la differenza." dark />
          <div className="mt-6 space-y-4 text-white/70 leading-relaxed" data-testid="metodo-narrativa-text">
            <p>Molti pazienti acquistano una calza elasto-compressiva senza una corretta rilevazione delle misure: la prescrizione indica la classe di compressione, ma non la taglia. Una calza sbagliata risulta troppo stretta o troppo larga, compromettendo l’efficacia della terapia e il comfort.</p>
            <p>Per questo Medisistem offre un <strong className="text-white">servizio professionale di misurazione e prova della calza</strong>, in sede o a domicilio, trasformando un semplice acquisto in un percorso di consulenza personalizzata.</p>
          </div>
          <Link to="/il-metodo" data-testid="home-metodo-cta" className="mt-8 inline-flex btn-green">
            Scopri il metodo completo (con video) <ArrowUpRight size={16} />
          </Link>
        </div>

        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {METHOD_FLOW.map((s, i) => {
            const Icon = METODO_ICONS[i];
            return (
              <div key={s.title} className="relative h-full rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm transition-[background-color,transform] duration-300 hover:bg-white/10 hover:-translate-y-1" data-testid={`metodo-step-${i + 1}`}>
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-green/15 text-green"><Icon size={22} /></span>
                <span className="mt-5 block font-serif text-sm font-bold text-green">0{i + 1}</span>
                <h3 className="mt-1 font-serif text-lg font-bold text-white leading-snug">{s.title}</h3>
                <p className="mt-2 text-sm text-white/55 leading-relaxed">{s.desc}</p>
                {i < METHOD_FLOW.length - 1 && (
                  <ArrowRight size={18} className="hidden lg:block absolute top-8 -right-3 text-green/50" />
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-12">
          <span className="eyebrow text-green/90">Perché è importante?</span>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {METHOD_BENEFITS.map((b) => (
              <div key={b} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-5 py-3.5 text-white/85">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green text-white"><Check size={12} strokeWidth={3} /></span>
                <span className="text-sm">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="py-20 lg:py-28 bg-cloud" data-testid="section-quickbooking">
      <div className="container-x">
        <QuickBooking />
      </div>
    </section>

    <section className="relative overflow-hidden py-20 lg:py-28" data-testid="metodo-quote">
      <div className="absolute inset-0" style={{ background: "linear-gradient(135deg,#1F5C48 0%,#175340 60%,#12362b 100%)" }} />
      <div className="absolute inset-0 grain-overlay" />
      <div className="container-x relative">
        <div className="max-w-4xl mx-auto text-center">
          <span className="font-serif text-6xl text-green leading-none">“</span>
          <blockquote className="mt-2 font-serif text-2xl sm:text-3xl lg:text-[2.25rem] leading-[1.3] font-medium text-white">
            La qualità di una calza elasto-compressiva non dipende solo dal prodotto, ma anche dalla corretta scelta della misura. Per questo Medisistem affianca ogni paziente con un servizio professionale di misurazione e prova, trasformando un semplice acquisto in un percorso di consulenza personalizzata.
          </blockquote>
          <p className="mt-8 text-sm uppercase tracking-[0.2em] text-white/60">Medisistem — Centro Specializzato</p>
        </div>
      </div>
    </section>
  </>
);

const Perche = () => (
  <section className="py-20 lg:py-28 bg-white" data-testid="section-perche">
    <div className="container-x">
      <SectionHeading eyebrow="Perché Medisistem" title="Otto ragioni per fidarti di noi." center />
      <Stagger className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {WHY.map((w, i) => {
          const Icon = WHY_ICONS[i];
          return (
            <StaggerItem key={w.title}>
              <div className="card-clean h-full p-7" data-testid={`perche-${i}`}>
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-green/10 text-green">
                  <Icon size={22} />
                </span>
                <h3 className="mt-5 font-serif text-lg font-bold text-navy">{w.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{w.desc}</p>
              </div>
            </StaggerItem>
          );
        })}
      </Stagger>
    </div>
  </section>
);

const Home = () => (
  <>
    <Hero />
    <Marquee items={["Terapia elasto-compressiva", "LUMAVEN", "SCHIEBLER", "Misurazione professionale", "Bendaggi", "Dal 1986", "Pressoterapia", "Assistenza continuativa"]} />
    <Dal1992 />
    <ChiSiamoHome />
    <Soluzioni />
    <BrandsStrip />
    <MetodoHome />
    <Perche />
    <Testimonials />
    <CertificationsHomeCallout />
    <CTASection />
  </>
);

export default Home;
